import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Crown, Medal, History, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, errMsg, fmtDate } from "../lib/api";
import { Avatar, Empty, Field, Loader, PageHeader, PillTabs } from "../components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const SeasonForm = ({ open, onOpenChange, season, onSaved }) => {
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF(season || { name: `Temporada ${new Date().getFullYear()}`, year: new Date().getFullYear(), description: "", active: false }); }, [open, season]);
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...f, year: Number(f.year) };
      if (season) await api.put(`/seasons/${season.id}`, body); else await api.post("/seasons", body);
      toast.success("Temporada salva"); onSaved(); onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-[#232B3E] bg-[#121722] sm:max-w-md">
        <DialogHeader><DialogTitle className="h-display text-3xl">{season ? "Editar temporada" : "Nova temporada"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-[1fr_100px] gap-3">
            <Field label="Nome"><input required data-testid="season-name-input" className="inp" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
            <Field label="Ano"><input required type="number" data-testid="season-year-input" className="inp" value={f.year || ""} onChange={(e) => setF({ ...f, year: e.target.value })} /></Field>
          </div>
          <Field label="Descrição"><input data-testid="season-description-input" className="inp" value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="season-active-checkbox" checked={!!f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />Temporada atual (novas etapas e pontos entram nela)</label>
          <button data-testid="season-save-btn" className="btn-lime w-full rounded-full py-3">Salvar</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const People = ({ list, icon: I, color, label }) => list.length > 0 && (
  <div className="flex flex-wrap items-center gap-2 text-sm">
    <span className={`flex w-24 items-center gap-1 text-xs font-semibold uppercase ${color}`}><I className="h-3.5 w-3.5" />{label}</span>
    {list.map((u) => <Link key={u.user_id} to={`/app/atletas/${u.user_id}`} className="flex items-center gap-1.5 hover:text-[#E4F222]"><Avatar user={u} size={26} />{u.name}</Link>)}
  </div>
);

export default function Seasons() {
  const { isAdmin } = useAuth();
  const [seasons, setSeasons] = useState(null);
  const [sel, setSel] = useState(null);
  const [data, setData] = useState(null);
  const [dlg, setDlg] = useState({ open: false, season: null });
  const loadSeasons = () => api.get("/seasons").then((r) => { setSeasons(r.data); setSel((s) => s || (r.data.find((x) => x.active) || r.data[0])?.id); });
  useEffect(() => { loadSeasons(); }, []);
  useEffect(() => { if (sel) { setData(null); api.get("/champions", { params: { season_id: sel } }).then((r) => setData(r.data)); } }, [sel]);
  if (!seasons) return <Loader />;
  const cur = seasons.find((s) => s.id === sel);
  const del = async () => {
    if (!window.confirm(`Excluir ${cur.name}?`)) return;
    try { await api.delete(`/seasons/${cur.id}`); setSel(null); loadSeasons(); toast.success("Temporada excluída"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader eyebrow="Hall da fama" title="Temporadas" subtitle="Histórico de campeões por etapa e por ano."
        action={isAdmin && <button data-testid="new-season-btn" onClick={() => setDlg({ open: true, season: null })} className="btn-lime flex items-center gap-2 rounded-full px-5 py-2.5 text-sm"><Plus className="h-4 w-4" />Nova temporada</button>} />
      <PillTabs testid="season-tab" tabs={seasons.map((s) => ({ value: s.id, label: `${s.name}${s.active ? " ●" : ""}` }))} value={sel} onChange={setSel} />
      {cur && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-[#94A3B8]">{cur.description || `Ano ${cur.year}`}{cur.active && <span className="ml-2 rounded-full bg-[#E4F222] px-2 py-0.5 text-[10px] font-bold text-black">ATUAL</span>}</p>
          {isAdmin && (
            <div className="flex gap-2">
              <button data-testid="edit-season-btn" onClick={() => setDlg({ open: true, season: cur })} className="btn-ghost flex items-center gap-1"><Pencil className="h-4 w-4" />Editar</button>
              <button data-testid="delete-season-btn" onClick={del} className="btn-ghost text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          )}
        </div>
      )}
      {!data ? <Loader /> : (
        <div className="mt-6 space-y-8">
          <div>
            <div className="h-display mb-3 text-3xl">Líderes da temporada</div>
            {data.season_leaders.length === 0 ? <p className="text-sm text-[#64748B]">Sem pontuação nesta temporada.</p> : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {data.season_leaders.map((l) => (
                  <div key={l.level} data-testid={`season-leader-${l.level}`} className="card-x p-4">
                    <div className="eyebrow mb-3 text-[#E4F222]">{l.level}</div>
                    {l.leaders.map((a, i) => (
                      <Link key={a.user_id} to={`/app/atletas/${a.user_id}`} className="flex items-center gap-3 py-1.5">
                        <span className="w-5 font-display text-lg font-black italic text-[#E4F222]">{i + 1}</span>
                        <Avatar user={a} size={30} /><span className="flex-1 truncate text-sm font-semibold">{a.name}</span><span className="text-xs text-[#94A3B8]">{a.total} pts</span>
                        {i === 0 && <Crown className="h-4 w-4 fill-[#E4F222] text-[#E4F222]" />}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="h-display mb-3 text-3xl">Campeões por etapa</div>
            {data.stages.length === 0 ? <Empty icon={History} title="Sem etapas" text="Nenhuma etapa nesta temporada." /> : (
              <div className="space-y-3">
                {data.stages.map(({ stage, levels }) => (
                  <div key={stage.id} data-testid={`champions-stage-${stage.id}`} className="card-x p-5">
                    <Link to={`/app/etapas/${stage.id}`} className="flex items-center gap-3">
                      <span className="font-display text-3xl font-black italic text-[#E4F222]">#{stage.number}</span>
                      <div><div className="h-display text-xl">{stage.name}</div><div className="flex items-center gap-1 text-xs text-[#64748B]"><CalendarDays className="h-3 w-3" />{fmtDate(stage.date)}</div></div>
                    </Link>
                    {levels.length === 0 ? <p className="mt-3 text-sm text-[#64748B]">Resultados ainda não lançados.</p> : (
                      <div className="mt-4 grid gap-4 md:grid-cols-2">
                        {levels.map((l) => (
                          <div key={l.level} className="space-y-2 rounded-xl border border-[#232B3E] p-3">
                            <div className="text-xs font-bold uppercase tracking-wider text-[#94A3B8]">{l.level}</div>
                            <People list={l.campeao} icon={Crown} color="text-[#E4F222]" label="Campeões" />
                            <People list={l.vice} icon={Medal} color="text-slate-300" label="Vice" />
                            <People list={l.terceiro} icon={Medal} color="text-amber-600" label="3º lugar" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      <SeasonForm open={dlg.open} season={dlg.season} onOpenChange={(o) => setDlg({ ...dlg, open: o })} onSaved={loadSeasons} />
    </div>
  );
}
